/**
 * Live gospel-film playlist directory for the Reader.
 *
 * Initialised from the bundled seed (gospel-film-playlists.ts), then
 * refreshed from the Builder API on every startup. Result is cached in
 * localStorage (24-hour TTL) so repeat opens are instant and offline works.
 *
 * chapterVideo() reads `playlistDirectory.value` synchronously — the signal
 * update is transparent to callers; WatchLink re-renders automatically.
 */

import { signal } from '@preact/signals';
import type { GospelBook } from './gospel-film-playlists.js';
import { LUMO_PLAYLISTS } from './gospel-film-playlists.js';
import { PLAYLIST_THUMBNAILS } from './playlist-thumbnails.js';

export type PlaylistDirectory = {
  lumo: Record<string, Partial<Record<GospelBook, string>>>;
  fcbh: Record<string, Partial<Record<GospelBook, string>>>;
  /** playlist ID → first-video ID, used for thumbnail URLs. */
  thumbnails: Record<string, string>;
};

const CACHE_KEY = 'fcbh-gospel-playlists.v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const SEED: PlaylistDirectory = {
  lumo: LUMO_PLAYLISTS as Record<string, Partial<Record<GospelBook, string>>>,
  fcbh: {},
  thumbnails: PLAYLIST_THUMBNAILS,
};

function loadCache(): PlaylistDirectory | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: PlaylistDirectory; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    // Defensive: older cached snapshots may not have thumbnails yet.
    data.thumbnails ??= {};
    return data;
  } catch {
    return null;
  }
}

function saveCache(data: PlaylistDirectory): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* quota — ignore */
  }
}

export const playlistDirectory = signal<PlaylistDirectory>(loadCache() ?? SEED);

const BUILDER_BASE =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BUILDER_BASE_URL ??
  'https://fcbh-app-builder.vercel.app';

export async function fetchPlaylistDirectory(): Promise<void> {
  try {
    const res = await fetch(`${BUILDER_BASE}/api/playlists`, {
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) return;
    const data = (await res.json()) as PlaylistDirectory;
    if (!data?.lumo) return;
    playlistDirectory.value = data;
    saveCache(data);
  } catch {
    /* network failure or timeout — keep seed/cache */
  }
}
